import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import Storage from '../utils/storageES.js'
import HomeButton from './HomeButton.jsx'
import { aggregateProfile } from '../utils/profileManager.js'
import VoiceRecorder from './VoiceRecorder.jsx'
import licenseManager from '../utils/licenseManager.js'
import ThreadsResurface from './ThreadsResurface.jsx'
import PremiumGate from './PremiumGate.jsx'
import { haversineM, SERENDIPITY_THRESHOLD_M, GEO_OPTIONS } from '../utils/reveal.js'
import { optimizeImageFile } from '../utils/imageOptimizer.js'

// ── Sprint 1: recognised serendipity ────────────────────────────────────────
// One-shot device position, bounded so it never stalls a save. Resolves null on
// denial/failure/timeout — serendipity is a gift, never a requirement. The
// position becomes the memory's own geotag (note.coords) and feeds the off-route
// flag. It is never transmitted; the continuous proximity watch is never stored.
const getOneShotPosition = (timeoutMs = 8000) => new Promise((resolve) => {
  if (typeof navigator === 'undefined' || !('geolocation' in navigator)) return resolve(null)
  let done = false
  const finish = (v) => { if (!done) { done = true; resolve(v) } }
  const timer = setTimeout(() => finish(null), timeoutMs)
  navigator.geolocation.getCurrentPosition(
    (pos) => { clearTimeout(timer); finish({ lat: pos.coords.latitude, lng: pos.coords.longitude }) },
    () => { clearTimeout(timer); finish(null) },
    { ...GEO_OPTIONS, timeout: timeoutMs }
  )
})

// Planned POIs (with coords) for a trip day, read from the timeline (day{N}_period).
const plannedCoordsForDay = (timeline, tripDay) => {
  const out = []
  for (const [key, acts] of Object.entries(timeline || {})) {
    if (!key.startsWith(`day${tripDay}_`)) continue
    for (const a of (acts || [])) {
      if (typeof a.lat === 'number' && typeof a.lng === 'number') out.push({ lat: a.lat, lng: a.lng })
    }
  }
  return out
}

// Activity color mapping (same as in main app)
const getActivityColor = (category) => {
  const colors = {
    'food': '#E74C3C',           // Red - for food/restaurants
    'cultural': '#9B59B6',       // Purple - for museums/cultural sites
    'monuments': '#34495E',      // Dark blue-gray - for monuments/landmarks
    'activities': '#27AE60',     // Green - for activities/sports
    'locals': '#F39C12',         // Orange - for local experiences
    'shopping': '#E67E22',       // Orange-red - for shopping
    'nature': '#16A085',         // Teal - for nature/parks
    'nightlife': '#8E44AD',      // Dark purple - for nightlife
    'transport': '#3498DB',      // Blue - for transportation
    'default': '#D4A574'         // Default brown - fallback
  }
  return colors[category] || colors['default']
}

function NotePhoto({ photo }) {
  const [src, setSrc] = useState(photo.data || null)

  useEffect(() => {
    if (src || !photo.id) return
    Storage.getPhotoById(photo.id).then(record => {
      if (record?.data) setSrc(record.data)
    })
  }, [photo.id])

  if (!src) {
    return (
      <div style={{ width: '100%', height: '100%', background: '#e8e1d6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ color: '#B8A082', fontSize: '18px' }}>◌</span>
      </div>
    )
  }

  return (
    <img
      src={src}
      alt="Memory"
      style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer' }}
      onClick={() => {
        const w = window.open()
        w.document.write(`<img src="${src}" style="max-width:100%;max-height:100vh;object-fit:contain;" />`)
      }}
    />
  )
}

function Notes({ userData, onNavigate }) {
  const { t, i18n } = useTranslation()
  const [currentView, setCurrentView] = useState('main') // main, writeMemory, writeReflection, bookView
  const [notes, setNotes] = useState([])
  const [reflections, setReflections] = useState([])
  const [selectedDay, setSelectedDay] = useState(null)
  const [editingMemory, setEditingMemory] = useState(null)
  const [expandedDays, setExpandedDays] = useState([1]) // Track which days are expanded (default: day 1)
  const [showDayPicker, setShowDayPicker] = useState(false)
  const [selectedWriteDay, setSelectedWriteDay] = useState(null)
  const [prefilledTitle, setPrefilledTitle] = useState(null)
  const [pendingActivityId, setPendingActivityId] = useState(null)
  const [highlightActivityId, setHighlightActivityId] = useState(null)

  useEffect(() => {
    loadNotesData()

    // Check for navigation options to go directly to write memory
    if (window.navigationOptions?.directToWriteMemory) {
      if (window.navigationOptions.preselectedDay) {
        setSelectedWriteDay(window.navigationOptions.preselectedDay)
        if (window.navigationOptions.prefilledTitle) setPrefilledTitle(window.navigationOptions.prefilledTitle)
        if (window.navigationOptions.activityId) setPendingActivityId(window.navigationOptions.activityId)
        setCurrentView('writeMemory')
      } else {
        setShowDayPicker(true)
      }
      delete window.navigationOptions
    }

    // Check for navigation options to go directly to book view
    if (window.navigationOptions?.directToBookView) {
      const getCurrentTripDay = () => {
        if (!userData?.startDate) return 1
        const today = new Date()
        const tripStart = new Date(userData.startDate)
        const diffTime = today - tripStart
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1
        return Math.max(1, diffDays)
      }

      const day = window.navigationOptions.selectedDay || getCurrentTripDay()
      setSelectedDay(day)
      if (window.navigationOptions.activityId) setHighlightActivityId(window.navigationOptions.activityId)
      setCurrentView('bookView')
      // Clean up navigation options
      delete window.navigationOptions
    }
  }, [])

  const loadNotesData = () => {
    const tripData = Storage.getTripData()
    console.log('Loading notes data:', tripData.notes || [])
    console.log('Notes count:', (tripData.notes || []).length)
    console.log('Current trip day:', getTripDay())
    console.log('Today\'s notes:', (tripData.notes || []).filter(note => note.tripDay === getTripDay()))
    setNotes(tripData.notes || [])
    setReflections(tripData.reflections || [])
  }

  const getCurrentDate = () => {
    return new Date().toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  const getTripDay = () => {
    if (!userData?.startDate) {
      console.warn('No start date found in userData, defaulting to day 1')
      return 1
    }
    const today = new Date()
    const tripStart = new Date(userData.startDate)
    const diffTime = today - tripStart
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1
    const result = Math.max(1, diffDays)
    console.log(`Trip day calculation: today=${today.toDateString()}, start=${tripStart.toDateString()}, result=${result}`)
    return result
  }

  const groupNotesByDay = () => {
    const grouped = {}
    console.log('Grouping notes by day:', notes.length, 'notes total')
    notes.forEach(note => {
      const day = note.tripDay || 1
      if (!grouped[day]) grouped[day] = []
      grouped[day].push(note)
      console.log(`Note "${note.title}" assigned to day ${day}`)
    })
    console.log('Final grouped notes:', grouped)
    return grouped
  }

  const hasReflectionForDay = (day) => {
    return reflections.some(reflection => reflection.tripDay === day)
  }

  const onSubmitChallenge = (challengeNote) => {
    setCurrentView('challengeSubmission')
    setEditingMemory(challengeNote) // Reuse this state to store current challenge
  }

  if (currentView === 'challengeSubmission') {
    // Sprint 3 (PRIZE_GATE_MODE='claim'): the private prize submission is the
    // ONLY gated step. Accepting, scheduling, completing and sharing stay free.
    return (
      <PremiumGate feature="prize_challenges" onNavigate={onNavigate}>
        <ChallengeSubmissionForm
          challengeNote={editingMemory}
          onBack={() => {
            setEditingMemory(null)
            setCurrentView('main')
          }}
          onSubmit={() => {
            setEditingMemory(null)
            setCurrentView('main')
            loadNotesData() // Refresh notes data
          }}
          userData={userData}
        />
      </PremiumGate>
    )
  }

  if (currentView === 'writeMemory') {
    return <WriteMemory
      onBack={() => {
        setEditingMemory(null)
        setSelectedWriteDay(null)
        setPrefilledTitle(null)
        setPendingActivityId(null)
        setCurrentView('main')
      }}
      onSave={() => {
        setEditingMemory(null)
        setSelectedWriteDay(null)
        setPrefilledTitle(null)
        setPendingActivityId(null)
        loadNotesData()
        setCurrentView('main')
      }}
      userData={userData}
      editingMemory={editingMemory}
      preselectedDay={selectedWriteDay}
      prefilledTitle={prefilledTitle}
      pendingActivityId={pendingActivityId}
      onNavigate={onNavigate}
    />
  }

  if (currentView === 'writeReflection') {
    return <WriteReflection
      onBack={() => setCurrentView('main')}
      onSave={() => {
        loadNotesData()
        setCurrentView('main')
      }}
      userData={userData}
      onNavigate={onNavigate}
    />
  }

  if (currentView === 'bookView') {
    return <BookView
      notes={groupNotesByDay()}
      reflections={reflections}
      onBack={() => { setHighlightActivityId(null); setCurrentView('main') }}
      selectedDay={selectedDay}
      highlightActivityId={highlightActivityId}
      userData={userData}
      onEditMemory={(memory) => {
        setEditingMemory(memory)
        setCurrentView('writeMemory')
      }}
      onDeleteMemory={(memoryId) => {
        if (confirm(t('notes.deleteConfirm', 'Are you sure you want to delete this? This action cannot be undone.'))) {
          const tripData = Storage.getTripData()
          // Remove memory from notes
          tripData.notes = tripData.notes.filter(note => note.id !== memoryId)
          // Remove associated photos from global photos
          tripData.photos = (tripData.photos || []).filter(photo => photo.memoryId !== memoryId)
          // Activities share their id with the timeline entry — remove it there too,
          // so deleting an activity also removes it from the timeline.
          if (tripData.timeline) {
            Object.keys(tripData.timeline).forEach(key => {
              tripData.timeline[key] = (tripData.timeline[key] || []).filter(a => a.id !== memoryId)
            })
          }
          Storage.saveTripData(tripData)
          loadNotesData() // Reload data
        }
      }}
      onSubmitChallenge={onSubmitChallenge}
      onNavigate={onNavigate}
    />
  }

  return (
    <div className="container notes-page" style={{
      background: 'linear-gradient(135deg, #f5f1eb 0%, #ede4d3 100%)',
      minHeight: '100vh',
      padding: '20px',
      position: 'relative'
    }}>

      {/* Home Button */}
      <HomeButton onNavigate={onNavigate} />

      <div style={{ paddingTop: '60px' }}>
        <ThreadsResurface city={userData?.destination} activeTripId={userData?.tripId} />
      </div>

      {/* Header */}
      <div style={{
        textAlign: 'center',
        marginBottom: '30px'
      }}>
        <h1 style={{
          fontSize: '48px',
          fontWeight: '900',
          color: '#8B7355',
          margin: '0 0 8px 0',
          letterSpacing: '2px',
          textTransform: 'uppercase',
          fontFamily: "'Noto Serif', serif"
        }}>{t('notes.title')}</h1>
        <p style={{
          fontSize: '16px',
          color: '#8B7355',
          margin: '0 0 8px 0',
          opacity: '0.8'
        }}>{userData.destination?.toUpperCase() || t('notes.yourJourney')}</p>
        <p style={{
          fontSize: '14px',
          color: '#8B7355',
          margin: '0',
          opacity: '0.7'
        }}>{getCurrentDate()}</p>
      </div>

      {/* Quick Actions */}
      <div style={{
        display: 'flex',
        gap: '12px',
        marginBottom: '30px',
        justifyContent: 'center'
      }}>
        <button
          style={{
            background: '#C4714A',
            color: 'white',
            border: 'none',
            padding: '16px 24px',
            borderRadius: '25px',
            fontSize: '16px',
            fontWeight: '600',
            letterSpacing: '0.5px',
            cursor: 'pointer',
            flex: '1',
            maxWidth: '200px',
            boxShadow: '0 4px 12px rgba(184, 160, 130, 0.3)'
          }}
          onClick={() => setShowDayPicker(true)}
        >
          {t('notes.writeMemory')}
        </button>

        <button
          style={{
            background: '#C4714A',
            color: 'white',
            border: 'none',
            padding: '16px 24px',
            borderRadius: '25px',
            fontSize: '16px',
            fontWeight: '600',
            letterSpacing: '0.5px',
            cursor: 'pointer',
            flex: '1',
            maxWidth: '200px',
            boxShadow: '0 4px 12px rgba(212, 165, 116, 0.3)'
          }}
          onClick={() => setCurrentView('writeReflection')}
        >
          {t('notes.todayReflection')}
        </button>
      </div>

      {/* Trip Journal Book */}
      <div style={{
        background: 'white',
        borderRadius: '20px',
        padding: '30px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        marginBottom: '20px',
        minHeight: '400px'
      }}>
        
        {/* Page Title */}
        <div style={{
          textAlign: 'center',
          marginBottom: '30px',
          borderBottom: '2px solid #f0f0f0',
          paddingBottom: '20px'
        }}>
          <h2 style={{
            fontSize: '28px',
            fontWeight: '700',
            color: '#8B7355',
            margin: '0 0 8px 0'
          }}>{t('notes.travelJournal')}</h2>
          <p style={{
            fontSize: '14px',
            color: '#999',
            margin: '0',
            fontStyle: 'italic'
          }}>{t('notes.completeView', { days: (() => {
            const tripDuration = userData?.endDate && userData?.startDate
              ? Math.ceil((new Date(userData.endDate) - new Date(userData.startDate)) / (1000 * 60 * 60 * 24)) + 1
              : userData?.totalDays || 7;
            return tripDuration;
          })() })}</p>
        </div>

        {/* Accordion - All Days */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {(() => {
            // Calculate trip duration from dates if totalDays is not available
            const tripDuration = userData?.endDate && userData?.startDate
              ? Math.ceil((new Date(userData.endDate) - new Date(userData.startDate)) / (1000 * 60 * 60 * 24)) + 1
              : userData?.totalDays || 7;

            console.log('[Notes Accordion] userData:', userData);
            console.log('[Notes Accordion] totalDays:', userData?.totalDays);
            console.log('[Notes Accordion] startDate:', userData?.startDate);
            console.log('[Notes Accordion] endDate:', userData?.endDate);
            console.log('[Notes Accordion] Calculated tripDuration:', tripDuration);
            return null;
          })()}
          {Array.from({ length: (() => {
            const tripDuration = userData?.endDate && userData?.startDate
              ? Math.ceil((new Date(userData.endDate) - new Date(userData.startDate)) / (1000 * 60 * 60 * 24)) + 1
              : userData?.totalDays || 7;
            return tripDuration;
          })() }, (_, index) => {
            const day = index + 1;
            const dayNotes = notes.filter(note => note.tripDay === day);
            const hasReflection = hasReflectionForDay(day);
            const isExpanded = expandedDays.includes(day);
            const isToday = day === getTripDay();

            // Group notes by type
            const activities = dayNotes.filter(n => n.isActivity === true);
            const challenges = dayNotes.filter(n => n.isChallenge === true);
            const personalNotes = dayNotes.filter(n => !n.isActivity && !n.isChallenge && n.type !== 'reflection');

            return (
              <div
                key={day}
                style={{
                  background: 'white',
                  borderRadius: '12px',
                  border: isToday ? '2px solid #B8A082' : '1px solid #e8e1d6',
                  overflow: 'hidden'
                }}
              >
                {/* Day Header - Clickable */}
                <div
                  onClick={() => {
                    setExpandedDays(prev =>
                      prev.includes(day)
                        ? prev.filter(d => d !== day)
                        : [...prev, day]
                    );
                  }}
                  style={{
                    padding: '16px 20px',
                    background: isToday ? '#f9f7f4' : '#fafafa',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    transition: 'background 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = isToday ? '#f5f1eb' : '#f5f5f5';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = isToday ? '#f9f7f4' : '#fafafa';
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{
                      fontSize: '20px',
                      fontWeight: '700',
                      color: isToday ? '#5B8FA8' : '#8B7355'
                    }}>
                      {isToday ? '📍' : '📅'} Day {day}
                    </div>
                    {isToday && (
                      <span style={{
                        background: '#C4714A',
                        color: 'white',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: '10px',
                        fontWeight: '600',
                        textTransform: 'uppercase'
                      }}>
                        {t('notes.today')}
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div style={{ display: 'flex', gap: '12px', fontSize: '12px', color: '#666' }}>
                      <span>📝 {dayNotes.length}</span>
                      <span>{hasReflection ? '✅' : '⭕'}</span>
                    </div>
                    <div style={{
                      fontSize: '20px',
                      transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.3s ease'
                    }}>
                      ▼
                    </div>
                  </div>
                </div>

                {/* Day Content - Expandable */}
                {isExpanded && (
                  <div style={{ padding: '20px' }}>
                    {/* Activities Section */}
                    {activities.length > 0 && (
                      <div style={{ marginBottom: '20px' }}>
                        <h4 style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#8B7355',
                          margin: '0 0 12px 0',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          🎯 {t('notes.activities')} ({activities.length})
                        </h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {activities.map(note => (
                            <div
                              key={note.id}
                              style={{
                                background: '#f9f7f4',
                                padding: '12px',
                                borderRadius: '8px',
                                borderLeft: '3px solid #B8A082',
                                cursor: 'pointer'
                              }}
                              onClick={() => {
                                setSelectedDay(day);
                                setCurrentView('bookView');
                              }}
                            >
                              <div style={{ fontWeight: '600', color: '#8B7355', fontSize: '14px' }}>
                                {note.title}
                              </div>
                              {note.content && (
                                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px', lineHeight: '1.4' }}>
                                  {note.content.substring(0, 80)}{note.content.length > 80 ? '...' : ''}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Challenges Section */}
                    {challenges.length > 0 && (
                      <div style={{ marginBottom: '20px' }}>
                        <h4 style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#8B7355',
                          margin: '0 0 12px 0',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          🏆 {t('home.challenges')} ({challenges.length})
                        </h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {challenges.map(note => (
                            <div
                              key={note.id}
                              style={{
                                background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                                padding: '12px',
                                borderRadius: '8px',
                                cursor: 'pointer'
                              }}
                              onClick={() => {
                                setSelectedDay(day);
                                setCurrentView('bookView');
                              }}
                            >
                              <div style={{ fontWeight: '600', color: '#333', fontSize: '14px' }}>
                                {note.title}
                              </div>
                              {note.challengeData && (
                                <div style={{ fontSize: '11px', color: '#555', marginTop: '4px' }}>
                                  🎁 Prize: {note.challengeData.prize}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Personal Notes Section */}
                    {personalNotes.length > 0 && (
                      <div style={{ marginBottom: '20px' }}>
                        <h4 style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#8B7355',
                          margin: '0 0 12px 0',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          📝 {t('home.notes')} ({personalNotes.length})
                        </h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {personalNotes.map(note => (
                            <div
                              key={note.id}
                              style={{
                                background: '#f0f0f0',
                                padding: '12px',
                                borderRadius: '8px',
                                cursor: 'pointer'
                              }}
                              onClick={() => {
                                setSelectedDay(day);
                                setCurrentView('bookView');
                              }}
                            >
                              <div style={{ fontWeight: '600', color: '#8B7355', fontSize: '14px' }}>
                                {note.title}
                              </div>
                              {note.content && (
                                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px', lineHeight: '1.4' }}>
                                  {note.content.substring(0, 80)}{note.content.length > 80 ? '...' : ''}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Reflection Status */}
                    <div style={{
                      background: hasReflection ? '#e8f5e9' : '#fff3e0',
                      padding: '12px',
                      borderRadius: '8px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '16px'
                    }}>
                      <span style={{ fontSize: '14px', color: '#666' }}>
                        {hasReflection ? `✅ ${t('notes.reflectionComplete')}` : `⭕ ${t('notes.noReflection')}`}
                      </span>
                    </div>

                    {/* Empty State */}
                    {dayNotes.length === 0 && !hasReflection && (
                      <div style={{
                        textAlign: 'center',
                        padding: '30px 20px',
                        color: '#999',
                        fontStyle: 'italic'
                      }}>
                        <p>{t('notes.noNotesDay')}</p>
                      </div>
                    )}

                    {/* Action Button */}
                    <button
                      style={{
                        background: '#C4714A',
                        color: 'white',
                        border: 'none',
                        padding: '10px 16px',
                        borderRadius: '8px',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        width: '100%'
                      }}
                      onClick={() => {
                        setSelectedDay(day);
                        setCurrentView('bookView');
                      }}
                    >
                      {t('notes.viewChapter')}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Back Button */}
      <div style={{
        textAlign: 'center'
      }}>
        <button
          style={{
            background: 'transparent',
            color: '#8B7355',
            border: '2px solid #8B7355',
            padding: '12px 24px',
            borderRadius: '25px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
          onClick={() => onNavigate('home')}
        >
          {t('navigation.backToHome')}
        </button>
      </div>

      {/* Day Picker Modal */}
      {showDayPicker && (() => {
        const currentTripDay = getTripDay()
        const tripDuration = userData?.endDate && userData?.startDate
          ? Math.ceil((new Date(userData.endDate) - new Date(userData.startDate)) / (1000 * 60 * 60 * 24)) + 1
          : userData?.totalDays || 7

        // Build week structure identical to main page timeline
        const getWeekStructure = () => {
          if (!userData?.startDate || !userData?.endDate) {
            // Fallback: simple list of days when no dates available
            return null
          }
          const startDate = new Date(userData.startDate)
          const endDate = new Date(userData.endDate)

          const firstMonday = new Date(startDate)
          const dayOfWeek = firstMonday.getDay()
          const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1
          firstMonday.setDate(firstMonday.getDate() - daysToSubtract)

          const lastSunday = new Date(endDate)
          const endDayOfWeek = lastSunday.getDay()
          const daysToAdd = endDayOfWeek === 0 ? 0 : 7 - endDayOfWeek
          lastSunday.setDate(lastSunday.getDate() + daysToAdd)

          const weeks = []
          const currentWeekStart = new Date(firstMonday)
          const dayLabels = [
            t('mainPage.daysOfWeek.MON'), t('mainPage.daysOfWeek.TUE'), t('mainPage.daysOfWeek.WED'),
            t('mainPage.daysOfWeek.THU'), t('mainPage.daysOfWeek.FRI'), t('mainPage.daysOfWeek.SAT'),
            t('mainPage.daysOfWeek.SUN')
          ]

          while (currentWeekStart <= lastSunday) {
            const weekEnd = new Date(currentWeekStart)
            weekEnd.setDate(weekEnd.getDate() + 6)
            const hasTrip = !(weekEnd < startDate || currentWeekStart > endDate)

            if (hasTrip) {
              const week = {
                weekNumber: weeks.length + 1,
                startDate: new Date(currentWeekStart),
                endDate: new Date(weekEnd),
                days: []
              }
              for (let i = 0; i < 7; i++) {
                const dayDate = new Date(currentWeekStart)
                dayDate.setDate(dayDate.getDate() + i)
                const isInTrip = dayDate >= startDate && dayDate <= endDate
                const tripDay = isInTrip
                  ? Math.floor((dayDate - startDate) / (1000 * 60 * 60 * 24)) + 1
                  : null

                const today = new Date()
                today.setHours(0, 0, 0, 0)
                const checkDate = new Date(dayDate)
                checkDate.setHours(0, 0, 0, 0)

                week.days.push({
                  date: dayDate,
                  dayOfWeek: dayLabels[i],
                  tripDay,
                  isInTrip,
                  isToday: checkDate.getTime() === today.getTime(),
                  isSelectable: isInTrip && tripDay <= currentTripDay
                })
              }
              weeks.push(week)
            }
            currentWeekStart.setDate(currentWeekStart.getDate() + 7)
          }
          return weeks
        }

        const weeks = getWeekStructure()

        return (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.55)',
              zIndex: 1000,
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'center'
            }}
            onClick={(e) => { if (e.target === e.currentTarget) setShowDayPicker(false) }}
          >
            <div style={{
              background: 'linear-gradient(135deg, #f5f1eb 0%, #ede4d3 100%)',
              borderRadius: '24px 24px 0 0',
              width: '100%',
              maxWidth: '600px',
              maxHeight: '88vh',
              overflowY: 'auto',
              padding: '28px 20px 32px',
              boxShadow: '0 -8px 40px rgba(0,0,0,0.2)'
            }}>

              {/* Handle bar */}
              <div style={{
                width: '40px',
                height: '4px',
                background: '#C4A882',
                borderRadius: '2px',
                margin: '0 auto 24px'
              }} />

              {/* Title */}
              <div style={{ textAlign: 'center', marginBottom: '6px' }}>
                <h2 style={{
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#B8A082',
                  letterSpacing: '1px',
                  textTransform: 'uppercase',
                  margin: '0 0 6px 0'
                }}>{t('notes.chooseDayTitle')}</h2>
                <p style={{
                  fontSize: '14px',
                  color: '#8B7355',
                  margin: '0 0 20px 0',
                  opacity: '0.85'
                }}>{t('notes.chooseDaySubtitle')}</p>
              </div>

              {/* Calendar grid (week-based, same as main page timeline) */}
              {weeks ? weeks.map((week, weekIndex) => (
                <div key={weekIndex} style={{ marginBottom: '20px' }}>
                  {/* Week header */}
                  <div style={{
                    fontSize: '11px',
                    fontWeight: '700',
                    color: '#B8A082',
                    marginBottom: '8px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    {t('mainPage.week')} {week.weekNumber}: {week.startDate.toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' })} — {week.endDate.toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' })}
                  </div>

                  {/* 7-column grid */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(7, 1fr)',
                    gap: '0',
                    background: 'white',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }}>
                    {week.days.map((day, dayIndex) => {
                      const isSelected = selectedWriteDay === day.tripDay
                      return (
                        <div
                          key={dayIndex}
                          onClick={() => {
                            if (!day.isSelectable) return
                            setSelectedWriteDay(day.tripDay)
                            setShowDayPicker(false)
                            setCurrentView('writeMemory')
                          }}
                          style={{
                            borderRight: dayIndex < 6 ? '1px solid rgba(0,0,0,0.05)' : 'none',
                            padding: '10px 4px',
                            background: isSelected
                              ? 'rgb(139 115 85 / 25%)'
                              : day.isToday
                                ? 'rgb(201 137 57 / 50%)'
                                : day.isInTrip
                                  ? 'rgb(225 147 50 / 21%)'
                                  : 'rgba(0,0,0,0.03)',
                            opacity: day.isInTrip ? (day.isSelectable ? 1 : 0.45) : 0.3,
                            cursor: day.isSelectable ? 'pointer' : 'default',
                            transition: 'background 0.15s ease',
                            outline: isSelected ? '2px solid #8B7355' : 'none',
                            outlineOffset: '-2px',
                            position: 'relative'
                          }}
                          onMouseEnter={(e) => {
                            if (day.isSelectable && !isSelected)
                              e.currentTarget.style.background = 'rgb(201 137 57 / 35%)'
                          }}
                          onMouseLeave={(e) => {
                            if (day.isSelectable && !isSelected) {
                              e.currentTarget.style.background = day.isToday
                                ? 'rgb(201 137 57 / 50%)'
                                : 'rgb(225 147 50 / 21%)'
                            }
                          }}
                        >
                          {/* Day of week */}
                          <div style={{
                            textAlign: 'center',
                            fontSize: '9px',
                            fontWeight: '700',
                            color: '#8B7355',
                            letterSpacing: '0.5px',
                            marginBottom: '2px'
                          }}>{day.dayOfWeek}</div>

                          {/* Date number */}
                          <div style={{
                            textAlign: 'center',
                            fontSize: '11px',
                            color: '#333',
                            fontWeight: '600',
                            marginBottom: '2px'
                          }}>{day.date.toLocaleDateString('en-US', { day: 'numeric' })}</div>

                          {/* Trip day label */}
                          {day.isInTrip && (
                            <div style={{
                              textAlign: 'center',
                              fontSize: '8px',
                              color: day.isToday ? '#8B7355' : '#B8A082',
                              fontWeight: '700'
                            }}>
                              {day.isToday ? '📍' : ''}{t('mainPage.day')} {day.tripDay}
                            </div>
                          )}

                          {/* Future indicator */}
                          {day.isInTrip && !day.isSelectable && (
                            <div style={{
                              textAlign: 'center',
                              fontSize: '7px',
                              color: '#B8A082',
                              marginTop: '2px',
                              fontStyle: 'italic'
                            }}>{t('notes.futureDay')}</div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
              )) : (
                /* Fallback: simple day list when no start/end dates */
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: '8px',
                  marginBottom: '20px'
                }}>
                  {Array.from({ length: Math.min(currentTripDay, tripDuration) }, (_, i) => i + 1).map(day => (
                    <button
                      key={day}
                      onClick={() => {
                        setSelectedWriteDay(day)
                        setShowDayPicker(false)
                        setCurrentView('writeMemory')
                      }}
                      style={{
                        background: day === currentTripDay ? 'rgb(201 137 57 / 50%)' : 'rgb(225 147 50 / 21%)',
                        border: '1px solid rgba(0,0,0,0.05)',
                        borderRadius: '8px',
                        padding: '12px 4px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: '700',
                        color: '#8B7355'
                      }}
                    >
                      {t('mainPage.day')} {day}
                      {day === currentTripDay && <div style={{ fontSize: '8px' }}>📍</div>}
                    </button>
                  ))}
                </div>
              )}

              {/* Cancel */}
              <button
                onClick={() => setShowDayPicker(false)}
                style={{
                  display: 'block',
                  width: '100%',
                  marginTop: '8px',
                  padding: '14px',
                  background: 'transparent',
                  border: '2px solid #C4A882',
                  borderRadius: '20px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#8B7355',
                  cursor: 'pointer',
                  letterSpacing: '0.5px'
                }}
              >
                {t('navigation.cancel') || 'Cancel'}
              </button>
            </div>
          </div>
        )
      })()}
    </div>
  )
}

// Write Memory Component
function WriteMemory({ onBack, onSave, userData, editingMemory, preselectedDay, prefilledTitle, pendingActivityId, onNavigate }) {
  const { t } = useTranslation()
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [tags, setTags] = useState([])
  const [photos, setPhotos] = useState([])

  // Load existing memory data if editing
  useEffect(() => {
    if (editingMemory) {
      // For a scheduled activity ("TO DO" note), strip the "TO DO:" prefix and the
      // auto "Scheduled for Day…" placeholder so the user starts on a clean canvas
      // to add their own text + photos.
      const isActivityNote = editingMemory.isActivity === true
      setTitle((editingMemory.title || '').replace(/^TO DO:\s*/i, ''))
      setContent(isActivityNote && /^Scheduled for Day/i.test(editingMemory.content || '')
        ? '' : (editingMemory.content || ''))
      setTags(editingMemory.tags || [])
      setPhotos(editingMemory.photos || [])
    } else {
      setTitle(prefilledTitle || '')
      setContent('')
      setTags([])
      setPhotos([])
    }
  }, [editingMemory, prefilledTitle])

  // Preset tags: 3 positive, 3 negative
  const availableTags = [
    t('notes.experienceTags.amazing'), t('notes.experienceTags.worthVisiting'), t('notes.experienceTags.highlyRecommend'),
    t('notes.experienceTags.disappointing'), t('notes.experienceTags.overrated'), t('notes.experienceTags.notWorthIt')
  ]

  const getTripDay = () => {
    if (!userData?.startDate) {
      console.warn('WriteMemory: No start date found in userData, defaulting to day 1')
      return 1
    }
    const today = new Date()
    const tripStart = new Date(userData.startDate)
    const diffTime = today - tripStart
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1
    const result = Math.max(1, diffDays)
    console.log(`WriteMemory - Trip day calculation: today=${today.toDateString()}, start=${tripStart.toDateString()}, result=${result}`)
    return result
  }

  const addTag = (tag) => {
    if (tag && !tags.includes(tag)) {
      setTags([...tags, tag])
    }
  }

  const removeTag = (tagToRemove) => {
    setTags(tags.filter(tag => tag !== tagToRemove))
  }

  const handlePhotoUpload = () => {
    // Create file input for gallery upload
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/*'
    input.multiple = true
    // Remove capture attribute to allow gallery selection
    input.onchange = (e) => {
      const files = Array.from(e.target.files)
      // Optimize each photo at ingest (downscale + WebP/JPEG) so storage and
      // backups stay light. The phone's original is untouched.
      files.forEach(async (file) => {
        const data = await optimizeImageFile(file)
        if (!data) return
        setPhotos(prev => [...prev, {
          id: Date.now() + Math.random(),
          data,
          name: file.name,
          timestamp: new Date().toISOString()
        }])
      })
    }
    input.click()
  }

  const handlePhotoCapture = () => {
    // Create file input specifically for camera
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/*'
    input.capture = 'environment' // Force camera
    input.onchange = async (e) => {
      const file = e.target.files[0]
      if (!file) return
      const data = await optimizeImageFile(file)
      if (!data) return
      setPhotos(prev => [...prev, {
        id: Date.now(),
        data,
        name: file.name,
        timestamp: new Date().toISOString()
      }])
    }
    input.click()
  }

  const removePhoto = (photoId) => {
    setPhotos(photos.filter(photo => photo.id !== photoId))
  }

  const handleSave = async () => {
    if (!title.trim() || !content.trim()) {
      alert('Please fill in both title and content')
      return
    }

    const memory = {
      id: editingMemory ? editingMemory.id : Date.now(),
      title: title.trim(),
      content: content.trim(),
      tags: tags,
      photos: photos.map(p => ({ id: p.id, timestamp: p.timestamp })),
      tripDay: editingMemory ? editingMemory.tripDay : (preselectedDay ?? getTripDay()),
      date: editingMemory ? editingMemory.date : new Date().toISOString(),
      timestamp: editingMemory ? editingMemory.timestamp : new Date().toISOString(), // Added for PDF compatibility
      location: userData.destination || ''
    }

    const tripData = Storage.getTripData()

    if (editingMemory) {
      // Update existing memory. Spread the original first so activity/challenge
      // fields (isActivity, mapsLink, activityCategory, scheduled*, type, offRoute,
      // coords…) survive — the form's edited fields then override on top. This is
      // what lets a scheduled "TO DO" activity note gain real text + photos.
      const index = tripData.notes.findIndex(note => note.id === editingMemory.id)
      if (index !== -1) {
        tripData.notes[index] = { ...editingMemory, ...memory }
      }

    } else {
      // Geotag the memory + recognised serendipity. One-shot position: stored as
      // the memory's own place (memory.coords), and compared to the day's planned
      // POIs to quietly mark it off-route if it's >250 m from all of them.
      try {
        const here = await getOneShotPosition()
        if (here) {
          memory.coords = { lat: here.lat, lng: here.lng }
          const planned = plannedCoordsForDay(tripData.timeline, memory.tripDay)
          if (planned.length > 0) {
            const minD = Math.min(...planned.map(p => haversineM(here.lat, here.lng, p.lat, p.lng)))
            if (minD > SERENDIPITY_THRESHOLD_M) memory.offRoute = true
          }
        }
      } catch { /* serendipity is a gift, never a requirement */ }

      // Add new memory
      tripData.notes = [...(tripData.notes || []), memory]
    }

    // Save trip data (notes, reflections, etc.) to IndexedDB
    await Storage.saveTripDataAsync(tripData)
    console.log('📸 [Notes] Saved trip data to IndexedDB');

    // Save photos to IndexedDB separately (NEW - critical for photo persistence!)
    if (photos.length > 0) {
      console.log('📸 [Notes] Now saving photos to IndexedDB...');
      await Storage.savePhotosToIndexedDB(photos, memory.title, memory.id);
      console.log('📸 [Notes] Photos saved to IndexedDB');
    }

    // Debug: Check what was saved
    console.log('Memory saved:', memory)
    console.log('Total notes after save:', tripData.notes.length)
    console.log('Trip day assigned:', memory.tripDay)
    console.log('All notes after save:', tripData.notes.map(n => ({ id: n.id, title: n.title, tripDay: n.tripDay })))

    // Mark the originating activity as visited if this note was triggered from DayDetailModal
    if (pendingActivityId) {
      const td = Storage.getTripData()
      let changed = false
      Object.keys(td.timeline || {}).forEach(key => {
        td.timeline[key] = td.timeline[key].map(act => {
          if (act.id === pendingActivityId) { changed = true; return { ...act, visited: true } }
          return act
        })
      })
      if (changed) {
        window.__cachedTripData = td
        Storage.saveTripData(td)
      }
    }

    aggregateProfile().catch(() => {})
    onSave()
  }

  return (
    <div className="container write-memory" style={{
      background: 'linear-gradient(135deg, #f5f1eb 0%, #ede4d3 100%)',
      minHeight: '100vh',
      padding: '20px',
      position: 'relative'
    }}>

      {/* Home Button */}
      <HomeButton onNavigate={onNavigate} />

      {/* Header */}
      <div style={{
        textAlign: 'center',
        marginBottom: '30px'
      }}>
        <h1 style={{
          fontSize: '36px',
          fontWeight: '900',
          color: '#8B7355',
          margin: '0 0 8px 0',
          letterSpacing: '-1px'
        }}>{editingMemory ? t('notes.editTitle') : t('notes.writeTitle')}</h1>
        <p style={{
          fontSize: '14px',
          color: '#8B7355',
          margin: '0',
          opacity: '0.7'
        }}>{t('notes.writingForDay', { day: editingMemory ? editingMemory.tripDay : (preselectedDay ?? getTripDay()) })} • {userData.destination?.toUpperCase() || t('notes.yourJourney')}</p>
      </div>

      {/* Form */}
      <div style={{
        background: 'white',
        borderRadius: '20px',
        padding: '30px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        maxWidth: '800px',
        margin: '0 auto'
      }}>
        
        {/* Title */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{
            display: 'block',
            fontSize: '16px',
            fontWeight: '600',
            color: '#8B7355',
            marginBottom: '8px'
          }}>{t('notes.memoryTitle')}</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t('notes.giveTitlePlaceholder')}
            style={{
              width: '100%',
              padding: '16px',
              border: '2px solid #f0f0f0',
              borderRadius: '12px',
              fontSize: '16px',
              fontFamily: 'inherit',
              outline: 'none',
              transition: 'border-color 0.2s ease'
            }}
            onFocus={(e) => e.target.style.borderColor = '#5B8FA8'}
            onBlur={(e) => e.target.style.borderColor = '#f0f0f0'}
          />
        </div>

        {/* Content */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{
            display: 'block',
            fontSize: '16px',
            fontWeight: '600',
            color: '#8B7355',
            marginBottom: '8px'
          }}>{t('notes.yourStory')}</label>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder={t('notes.writeExperiencePlaceholder')}
            rows={8}
            style={{
              width: '100%',
              padding: '16px',
              border: '2px solid #f0f0f0',
              borderRadius: '12px',
              fontSize: '16px',
              fontFamily: 'inherit',
              outline: 'none',
              resize: 'vertical',
              transition: 'border-color 0.2s ease'
            }}
            onFocus={(e) => e.target.style.borderColor = '#5B8FA8'}
            onBlur={(e) => e.target.style.borderColor = '#f0f0f0'}
          />

          {/* Voice Recording - Premium Feature */}
          <VoiceRecorder
            onTranscript={(text) => {
              const newContent = content ? `${content} ${text}` : text
              setContent(newContent)
            }}
            disabled={!licenseManager.hasFeatureAccess('voice_notes')}
            language="es"
          />
        </div>

        {/* Tags */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{
            display: 'block',
            fontSize: '16px',
            fontWeight: '600',
            color: '#8B7355',
            marginBottom: '8px'
          }}>{t('notes.addExperienceTag')}</label>
          
          <div style={{
            display: 'flex',
            gap: '8px',
            alignItems: 'center',
            marginBottom: '12px'
          }}>
            <select
              onChange={(e) => {
                if (e.target.value) {
                  addTag(e.target.value)
                  e.target.value = ''
                }
              }}
              style={{
                flex: '1',
                padding: '12px',
                border: '2px solid #f0f0f0',
                borderRadius: '8px',
                fontSize: '14px',
                outline: 'none',
                background: 'white',
                color: '#8B7355'
              }}
            >
              <option value="">{t('notes.chooseTag')}</option>
              <optgroup label={t('notes.tagPositive')}>
                <option value={t('notes.experienceTags.amazing')}>{t('notes.experienceTags.amazing')}</option>
                <option value={t('notes.experienceTags.worthVisiting')}>{t('notes.experienceTags.worthVisiting')}</option>
                <option value={t('notes.experienceTags.highlyRecommend')}>{t('notes.experienceTags.highlyRecommend')}</option>
              </optgroup>
              <optgroup label={t('notes.tagNegative')}>
                <option value={t('notes.experienceTags.disappointing')}>{t('notes.experienceTags.disappointing')}</option>
                <option value={t('notes.experienceTags.overrated')}>{t('notes.experienceTags.overrated')}</option>
                <option value={t('notes.experienceTags.notWorthIt')}>{t('notes.experienceTags.notWorthIt')}</option>
              </optgroup>
            </select>
          </div>

          {/* Selected Tags Display */}
          {tags.length > 0 && (
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '6px'
            }}>
              {tags.map((tag, index) => (
                <span
                  key={index}
                  style={{
                    background: tag.startsWith('Amazing') || tag.startsWith('Worth') || tag.startsWith('Highly') ? '#5B8FA8' : '#D85532',
                    color: 'white',
                    padding: '6px 12px',
                    borderRadius: '16px',
                    fontSize: '12px',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  {tag}
                  <button
                    type="button"
                    onClick={() => removeTag(tag)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'white',
                      cursor: 'pointer',
                      padding: '0',
                      fontSize: '14px'
                    }}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Photos */}
        <div style={{ marginBottom: '30px' }}>
          <label style={{
            display: 'block',
            fontSize: '16px',
            fontWeight: '600',
            color: '#8B7355',
            marginBottom: '12px'
          }}>{t('notes.addPhotos')}</label>
          
          {/* Photo Upload Buttons */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '12px',
            marginBottom: '16px'
          }}>
            <button
              type="button"
              onClick={handlePhotoCapture}
              style={{
                border: '2px dashed #D4A574',
                borderRadius: '12px',
                padding: '16px 12px',
                background: 'transparent',
                color: '#D4A574',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                transition: 'all 0.2s ease',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '8px'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#f9f7f4'
                e.target.style.borderColor = '#5B8FA8'
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'transparent'
                e.target.style.borderColor = '#D4A574'
              }}
            >
              <span style={{ fontSize: '24px' }}>📷</span>
              {t('notes.takePhoto')}
            </button>

            <button
              type="button"
              onClick={handlePhotoUpload}
              style={{
                border: '2px dashed #B8A082',
                borderRadius: '12px',
                padding: '16px 12px',
                background: 'transparent',
                color: '#B8A082',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                transition: 'all 0.2s ease',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '8px'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#f9f7f4'
                e.target.style.borderColor = '#8B7355'
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'transparent'
                e.target.style.borderColor = '#5B8FA8'
              }}
            >
              <span style={{ fontSize: '24px' }}>🖼️</span>
              {t('notes.uploadGallery')}
            </button>
          </div>

          {/* Photo Preview Grid */}
          {photos.length > 0 && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
              gap: '12px'
            }}>
              {photos.map((photo) => (
                <div
                  key={photo.id}
                  style={{
                    position: 'relative',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    aspectRatio: '1',
                    background: '#f0f0f0'
                  }}
                >
                  <img
                    src={photo.data}
                    alt="Memory"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => removePhoto(photo.id)}
                    style={{
                      position: 'absolute',
                      top: '4px',
                      right: '4px',
                      background: 'rgba(0,0,0,0.7)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '50%',
                      width: '24px',
                      height: '24px',
                      fontSize: '12px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Actions */}
        <div style={{
          display: 'flex',
          gap: '12px',
          justifyContent: 'center'
        }}>
          <button
            onClick={onBack}
            style={{
              background: 'transparent',
              color: '#8B7355',
              border: '2px solid #8B7355',
              padding: '16px 24px',
              borderRadius: '25px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              minWidth: '120px'
            }}
          >
            {t('buttons.cancel')}
          </button>

          <button
            onClick={handleSave}
            style={{
              background: '#C4714A',
              color: 'white',
              border: 'none',
              padding: '16px 24px',
              borderRadius: '25px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              minWidth: '120px',
              boxShadow: '0 4px 12px rgba(184, 160, 130, 0.3)'
            }}
          >
            {editingMemory ? t('notes.updateMemory') : t('notes.saveMemory')}
          </button>
        </div>
      </div>
    </div>
  )
}

// Write Reflection Component
function WriteReflection({ onBack, onSave, userData, onNavigate }) {
  const { t } = useTranslation()
  const [currentQuestion, setCurrentQuestion] = useState(0)
  const [answers, setAnswers] = useState(['', '', '', ''])

  const questions = [
    {
      question: t('notes.reflectionQuestion1'),
      placeholder: t('notes.reflectionPlaceholder1'),
      emoji: "😊"
    },
    {
      question: t('notes.reflectionQuestion2'),
      placeholder: t('notes.reflectionPlaceholder2'),
      emoji: "😮"
    },
    {
      question: t('notes.reflectionQuestion3'),
      placeholder: t('notes.reflectionPlaceholder3'),
      emoji: "🙏"
    },
    {
      question: t('notes.reflectionQuestion4'),
      placeholder: t('notes.reflectionPlaceholder4'),
      emoji: "🌅"
    }
  ]

  const getTripDay = () => {
    if (!userData?.startDate) {
      console.warn('WriteReflection: No start date found in userData, defaulting to day 1')
      return 1
    }
    const today = new Date()
    const tripStart = new Date(userData.startDate)
    const diffTime = today - tripStart
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1
    const result = Math.max(1, diffDays)
    console.log(`WriteReflection - Trip day calculation: today=${today.toDateString()}, start=${tripStart.toDateString()}, result=${result}`)
    return result
  }

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1)
    }
  }

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1)
    }
  }

  const handleSave = () => {
    const hasAnswers = answers.some(answer => answer.trim())
    if (!hasAnswers) {
      alert('Please answer at least one question')
      return
    }

    const reflection = {
      id: Date.now(),
      tripDay: getTripDay(),
      date: new Date().toISOString(),
      timestamp: new Date().toISOString(), // Added for PDF compatibility
      answers: {
        feeling: answers[0],
        surprise: answers[1],
        gratitude: answers[2],
        expectations: answers[3]
      }
    }

    const tripData = Storage.getTripData()
    // Remove existing reflection for this day if it exists
    tripData.reflections = (tripData.reflections || []).filter(r => r.tripDay !== getTripDay())
    tripData.reflections.push(reflection)
    // The reflection IS the day's closing — record the closure (additive, idempotent).
    tripData.dayClosures = { ...(tripData.dayClosures || {}), [getTripDay()]: new Date().toISOString() }
    Storage.saveTripData(tripData)

    // Day closed — a natural moment to offer a cloud copy (the nudge decides).
    try { window.dispatchEvent(new CustomEvent('wayfare-day-closed')) } catch (_) {}

    onSave()
  }

  const updateAnswer = (value) => {
    const newAnswers = [...answers]
    newAnswers[currentQuestion] = value
    setAnswers(newAnswers)
  }

  // Did the day wander off the planned route? Used only to colour the closing
  // prompt — never blocks, never judges.
  const hasOffRouteToday = (() => {
    try {
      const td = Storage.getTripData()
      const d = getTripDay()
      return (td.notes || []).some(n => n.offRoute && n.tripDay === d)
    } catch { return false }
  })()

  return (
    <div className="container write-reflection" style={{
      background: 'linear-gradient(135deg, #f5f1eb 0%, #ede4d3 100%)',
      minHeight: '100vh',
      padding: '20px',
      position: 'relative'
    }}>

      {/* Home Button */}
      <HomeButton onNavigate={onNavigate} />

      {/* Header */}
      <div style={{
        textAlign: 'center',
        marginBottom: '30px'
      }}>
        <h1 style={{
          fontSize: '36px',
          fontWeight: '900',
          color: '#8B7355',
          margin: '0 0 8px 0',
          letterSpacing: '-1px'
        }}>{t('notes.dailyReflectionTitle')}</h1>
        <p style={{
          fontSize: '14px',
          color: '#8B7355',
          margin: '0',
          opacity: '0.7'
        }}>{t('notes.dayProgress', { current: getTripDay(), total: '' })} • {t('notes.endOfDayReflection')}</p>
        {hasOffRouteToday && (
          <p style={{
            fontSize: '13px',
            color: '#2D5A6E',
            fontStyle: 'italic',
            margin: '10px auto 0',
            maxWidth: '420px',
            lineHeight: 1.5
          }}>🧭 {t('reveal.dayCloseOffRouteAddon')}</p>
        )}
      </div>

      {/* Progress */}
      <div style={{
        maxWidth: '600px',
        margin: '0 auto 30px',
        background: 'white',
        borderRadius: '12px',
        padding: '16px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '8px'
        }}>
          <span style={{
            fontSize: '14px',
            fontWeight: '600',
            color: '#8B7355'
          }}>
            {t('notes.question', { current: currentQuestion + 1, total: questions.length })}
          </span>
          <span style={{
            fontSize: '12px',
            color: '#999'
          }}>
            {t('notes.percentComplete', { percent: Math.round(((currentQuestion + 1) / questions.length) * 100) })}
          </span>
        </div>
        
        <div style={{
          width: '100%',
          height: '6px',
          background: '#f0f0f0',
          borderRadius: '3px',
          overflow: 'hidden'
        }}>
          <div style={{
            width: `${((currentQuestion + 1) / questions.length) * 100}%`,
            height: '100%',
            background: 'linear-gradient(90deg, #B8A082, #D4A574)',
            transition: 'width 0.3s ease'
          }}></div>
        </div>
      </div>

      {/* Question Form */}
      <div style={{
        background: 'white',
        borderRadius: '20px',
        padding: '40px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        maxWidth: '600px',
        margin: '0 auto'
      }}>
        
        <div style={{
          textAlign: 'center',
          marginBottom: '30px'
        }}>
          <div style={{
            fontSize: '48px',
            marginBottom: '16px'
          }}>
            {questions[currentQuestion].emoji}
          </div>
          
          <h2 style={{
            fontSize: '24px',
            fontWeight: '700',
            color: '#8B7355',
            margin: '0 0 16px 0'
          }}>
            {questions[currentQuestion].question}
          </h2>
        </div>

        <textarea
          value={answers[currentQuestion]}
          onChange={(e) => updateAnswer(e.target.value)}
          placeholder={questions[currentQuestion].placeholder}
          rows={6}
          style={{
            width: '100%',
            padding: '20px',
            border: '2px solid #f0f0f0',
            borderRadius: '16px',
            fontSize: '16px',
            fontFamily: 'inherit',
            outline: 'none',
            resize: 'vertical',
            marginBottom: '30px',
            transition: 'border-color 0.2s ease'
          }}
          onFocus={(e) => e.target.style.borderColor = '#5B8FA8'}
          onBlur={(e) => e.target.style.borderColor = '#f0f0f0'}
        />

        {/* Voice Recording - Premium Feature */}
        <VoiceRecorder
          onTranscript={(text) => {
            const currentAnswer = answers[currentQuestion] || ''
            const newAnswer = currentAnswer ? `${currentAnswer} ${text}` : text
            updateAnswer(newAnswer)
          }}
          disabled={!licenseManager.hasFeatureAccess('voice_notes')}
          language="es"
          style={{ marginBottom: '20px' }}
        />

        {/* Navigation */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <button
            onClick={onBack}
            style={{
              background: 'transparent',
              color: '#8B7355',
              border: '2px solid #8B7355',
              padding: '12px 20px',
              borderRadius: '20px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            {t('buttons.cancel')}
          </button>

          <div style={{
            display: 'flex',
            gap: '12px'
          }}>
            {currentQuestion > 0 && (
              <button
                onClick={handlePrevious}
                style={{
                  background: '#C4714A',
                  color: 'white',
                  border: 'none',
                  padding: '12px 20px',
                  borderRadius: '20px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                {t('navigation.previous')}
              </button>
            )}

            {currentQuestion < questions.length - 1 ? (
              <button
                onClick={handleNext}
                style={{
                  background: '#C4714A',
                  color: 'white',
                  border: 'none',
                  padding: '12px 20px',
                  borderRadius: '20px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                {t('navigation.next')}
              </button>
            ) : (
              <button
                onClick={handleSave}
                style={{
                  background: '#C4714A',
                  color: 'white',
                  border: 'none',
                  padding: '12px 20px',
                  borderRadius: '20px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(184, 160, 130, 0.3)'
                }}
              >
                {t('notes.saveReflection')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function ChallengeCard({ note, onSubmitChallenge, onDeleteMemory }) {
  const submitted  = note.challengeData?.submitted === true
  const approved   = note.challengeData?.approved === true
  const rejected   = note.challengeData?.rejected === true
  const points     = note.challengeData?.points
  const difficulty = note.challengeData?.difficulty

  const statusIcon  = approved ? '✅' : rejected ? '❌' : submitted ? '⏳' : '🎯'
  const statusLabel = approved ? 'Approved!' : rejected ? 'Not approved' : submitted ? 'In review…' : 'Pending'
  const cardBg = approved
    ? 'linear-gradient(135deg, #1a472a 0%, #27AE60 100%)'
    : rejected
    ? 'linear-gradient(135deg, #6d1a1a 0%, #b03030 100%)'
    : submitted
    ? 'linear-gradient(135deg, #2a3a5a 0%, #4a6fa5 100%)'
    : 'linear-gradient(135deg, #4a3520 0%, #8B5E2E 100%)'

  return (
    <div style={{
      background: cardBg,
      borderRadius: '16px',
      padding: '20px',
      color: 'white',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Decorative corner */}
      <div style={{ position: 'absolute', top: '-10px', right: '-10px', fontSize: '60px', opacity: 0.1 }}>🏆</div>

      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
        <span style={{ fontSize: '22px' }}>{statusIcon}</span>
        <span style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '1px', opacity: 0.8, textTransform: 'uppercase' }}>
          {statusLabel}
        </span>
        {points && (
          <span style={{
            marginLeft: 'auto',
            background: 'rgba(255,255,255,0.2)',
            borderRadius: '12px',
            padding: '3px 10px',
            fontSize: '12px',
            fontWeight: '700'
          }}>
            {points} pts
          </span>
        )}
      </div>

      {/* Title */}
      <div style={{ fontSize: '16px', fontWeight: '700', marginBottom: '6px', paddingRight: '40px' }}>
        {note.challengeData?.title || note.title}
      </div>

      {/* Description */}
      {note.challengeData?.description && (
        <div style={{ fontSize: '13px', opacity: 0.85, lineHeight: '1.5', marginBottom: '12px' }}>
          {note.challengeData.description}
        </div>
      )}

      {/* Badges row */}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: submitted ? '0' : '14px' }}>
        {difficulty && (
          <span style={{
            background: 'rgba(255,255,255,0.15)',
            borderRadius: '10px',
            padding: '3px 10px',
            fontSize: '11px',
            fontWeight: '600'
          }}>
            {difficulty}
          </span>
        )}
      </div>

      {/* Submit button / rejection reason */}
      {!submitted && (
        <button
          onClick={() => onSubmitChallenge(note)}
          style={{
            background: 'rgba(255,255,255,0.95)',
            color: '#8B5E2E',
            border: 'none',
            padding: '10px 20px',
            borderRadius: '20px',
            fontSize: '13px',
            fontWeight: '700',
            cursor: 'pointer',
            width: '100%'
          }}
        >
          Enviar para validación
        </button>
      )}
      {rejected && note.challengeData?.rejectionReason && (
        <div style={{ marginTop: '10px', background: 'rgba(255,255,255,0.12)', borderRadius: '10px', padding: '8px 12px', fontSize: '12px', opacity: 0.9 }}>
          {note.challengeData.rejectionReason}
        </div>
      )}

      {/* Delete button */}
      <button
        onClick={() => onDeleteMemory(note.id)}
        style={{
          position: 'absolute',
          top: '12px',
          right: '12px',
          background: 'rgba(255,255,255,0.15)',
          border: 'none',
          borderRadius: '50%',
          width: '28px',
          height: '28px',
          color: 'white',
          fontSize: '13px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        ✕
      </button>
    </div>
  )
}

// Book View Component
function BookView({ notes, reflections, onBack, selectedDay, highlightActivityId, userData, onEditMemory, onDeleteMemory, onSubmitChallenge, onNavigate }) {
  const { t } = useTranslation()
  const [currentDay, setCurrentDay] = useState(selectedDay || 1)
  const activityRefs = useRef({})

  // Sprint 2 — a sealed book is read-only: it's here to be read, not edited.
  const sealed = !!(Storage.getTripData()?.sealedAt)

  // Scroll to (and briefly pulse) the activity we navigated in for
  useEffect(() => {
    if (!highlightActivityId) return
    const el = activityRefs.current[highlightActivityId]
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [highlightActivityId, currentDay])

  const getDayReflection = (day) => {
    return reflections.find(reflection => reflection.tripDay === day)
  }

  const getDayNotes = (day) => {
    return notes[day] || []
  }

  const getDateForDay = (day) => {
    if (!userData.startDate) return 'Day ' + day
    const startDate = new Date(userData.startDate)
    const dayDate = new Date(startDate)
    dayDate.setDate(startDate.getDate() + day - 1)
    return dayDate.toLocaleDateString('en-US', { 
      weekday: 'long', 
      month: 'long', 
      day: 'numeric' 
    })
  }

  const availableDays = Math.max(...Object.keys(notes).map(Number), ...reflections.map(r => r.tripDay), 1)

  return (
    <div className="container book-view" style={{
      background: 'linear-gradient(135deg, #f5f1eb 0%, #ede4d3 100%)',
      minHeight: '100vh',
      padding: '20px',
      position: 'relative'
    }}>

      {/* Home Button */}
      <HomeButton onNavigate={onNavigate} />

      {sealed && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center',
          background: '#C8DCE5', color: '#2D5A6E', borderRadius: '12px',
          padding: '10px 14px', fontSize: '13px', fontWeight: 600, margin: '0 0 16px'
        }}>
          <span aria-hidden="true">📕</span> {t('seal.readOnlyNotice')}
        </div>
      )}

      {/* Header */}
      <div style={{
        textAlign: 'center',
        marginBottom: '30px'
      }}>
        <h1 style={{
          fontSize: '36px',
          fontWeight: '900',
          color: '#8B7355',
          margin: '0 0 8px 0',
          letterSpacing: '-1px'
        }}>{t('notes.travelJournalShort')}</h1>
        <p style={{
          fontSize: '14px',
          color: '#8B7355',
          margin: '0',
          opacity: '0.7'
        }}>{userData.destination?.toUpperCase() || t('notes.yourJourney')}</p>
      </div>

      {/* Day Navigation */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '16px',
        marginBottom: '30px'
      }}>
        <button
          onClick={() => setCurrentDay(Math.max(1, currentDay - 1))}
          disabled={currentDay <= 1}
          style={{
            background: currentDay <= 1 ? '#f0f0f0' : '#D4A574',
            color: currentDay <= 1 ? '#999' : 'white',
            border: 'none',
            width: '44px',
            height: '44px',
            borderRadius: '50%',
            fontSize: '18px',
            cursor: currentDay <= 1 ? 'not-allowed' : 'pointer'
          }}
        >
          ←
        </button>

        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '16px 24px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <h2 style={{
            fontSize: '24px',
            fontWeight: '700',
            color: '#8B7355',
            margin: '0 0 4px 0'
          }}>{t('notes.chapter', { number: currentDay })}</h2>
          <p style={{
            fontSize: '14px',
            color: '#999',
            margin: '0'
          }}>{getDateForDay(currentDay)}</p>
        </div>

        <button
          onClick={() => setCurrentDay(Math.min(availableDays, currentDay + 1))}
          disabled={currentDay >= availableDays}
          style={{
            background: currentDay >= availableDays ? '#f0f0f0' : '#D4A574',
            color: currentDay >= availableDays ? '#999' : 'white',
            border: 'none',
            width: '44px',
            height: '44px',
            borderRadius: '50%',
            fontSize: '18px',
            cursor: currentDay >= availableDays ? 'not-allowed' : 'pointer'
          }}
        >
          →
        </button>
      </div>

      {/* Chapter Content */}
      <div style={{
        background: 'white',
        borderRadius: '20px',
        padding: '40px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        marginBottom: '20px',
        minHeight: '400px',
        maxWidth: '800px',
        margin: '0 auto 20px'
      }}>

        {/* Activities Section */}
        {getDayNotes(currentDay).filter(n => n.isActivity === true).length > 0 && (
          <div style={{ marginBottom: '40px' }}>
            <h3 style={{
              fontSize: '20px',
              fontWeight: '700',
              color: '#8B7355',
              margin: '0 0 20px 0',
              borderBottom: '2px solid #f0f0f0',
              paddingBottom: '12px'
            }}>🎯 {t('notes.activities')}</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {getDayNotes(currentDay).filter(n => n.isActivity === true).map(note => {
                const activityColor = note.activityCategory ? getActivityColor(note.activityCategory) : '#B8A082'
                const isHighlighted = highlightActivityId && note.id === highlightActivityId
                return (
                  <div
                    key={note.id}
                    ref={el => { if (el) activityRefs.current[note.id] = el }}
                    style={{
                      background: isHighlighted ? '#fffaf0' : '#f9f7f4',
                      borderRadius: '16px',
                      padding: '24px',
                      border: `2px solid ${activityColor}`,
                      borderLeft: `6px solid ${activityColor}`,
                      position: 'relative',
                      boxShadow: isHighlighted ? `0 0 0 3px ${activityColor}55, 0 6px 20px rgba(0,0,0,0.12)` : 'none',
                      transition: 'box-shadow 0.3s ease, background 0.3s ease'
                    }}
                  >
                    {/* Edit (add text + photos) or delete this scheduled stop.
                        Deleting also removes it from the timeline. */}
                    {!sealed && (
                      <div style={{ position: 'absolute', top: '16px', right: '16px', display: 'flex', gap: '8px' }}>
                        <button
                          onClick={() => onEditMemory(note)}
                          style={{
                            background: '#C4714A', color: 'white', border: 'none',
                            borderRadius: '50%', width: '32px', height: '32px',
                            fontSize: '14px', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                          }}
                          title={t('notes.editMemory')}
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => onDeleteMemory(note.id)}
                          style={{
                            background: '#D85532', color: 'white', border: 'none',
                            borderRadius: '50%', width: '32px', height: '32px',
                            fontSize: '14px', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                          }}
                          title={t('notes.deleteMemory')}
                        >
                          🗑️
                        </button>
                      </div>
                    )}

                    <h4 style={{
                      fontSize: '18px',
                      fontWeight: '600',
                      color: activityColor,
                      margin: '0 0 12px 0',
                      paddingRight: '80px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      {note.title}
                    </h4>

                    {note.content && (
                      <p style={{
                        fontSize: '16px',
                        lineHeight: '1.6',
                        color: '#333',
                        margin: '0 0 16px 0',
                        whiteSpace: 'pre-wrap'
                      }}>{note.content}</p>
                    )}

                    {note.mapsLink && (
                      <a
                        href={note.mapsLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px',
                          background: 'linear-gradient(135deg, #27AE60 0%, #2ECC71 100%)',
                          color: 'white',
                          padding: '10px 18px',
                          borderRadius: '25px',
                          fontSize: '14px',
                          fontWeight: '600',
                          textDecoration: 'none'
                        }}
                        title={t('notes.openMaps')}
                      >
                        <span style={{ fontSize: '16px' }}>📍</span>
                        {t('notes.openMaps')}
                      </a>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* Memories Section */}
        <div style={{ marginBottom: '40px' }}>
          <h3 style={{
            fontSize: '20px',
            fontWeight: '700',
            color: '#8B7355',
            margin: '0 0 20px 0',
            borderBottom: '2px solid #f0f0f0',
            paddingBottom: '12px'
          }}>📝 {t('notes.memories')}</h3>

          {getDayNotes(currentDay).filter(n => !n.isActivity && !n.isChallenge).length > 0 ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '24px'
            }}>
              {getDayNotes(currentDay).filter(n => !n.isActivity && !n.isChallenge).map((note, index) => {
                // Get activity color based on category from tags or activityCategory
                const activityCategory = note.activityCategory || (note.tags && note.tags.find(tag =>
                  ['food', 'cultural', 'monuments', 'activities', 'locals', 'shopping', 'nature', 'nightlife', 'transport'].includes(tag)
                ))
                const activityColor = activityCategory ? getActivityColor(activityCategory) : null

                return (
                <div
                  key={note.id}
                  style={{
                    background: note.type === 'todo' ? '#fff7e6' : '#f9f7f4',
                    borderRadius: '16px',
                    padding: '24px',
                    border: activityColor ? `2px solid ${activityColor}` : (note.type === 'todo' ? '2px solid #f39c12' : '1px solid #e8e1d6'),
                    position: 'relative',
                    borderLeft: activityColor ? `6px solid ${activityColor}` : (note.type === 'todo' ? '6px solid #e67e22' : 'none')
                  }}
                >
                  {/* Action Buttons — hidden when the book is sealed (read-only) */}
                  {!sealed && (
                  <div style={{
                    position: 'absolute',
                    top: '16px',
                    right: '16px',
                    display: 'flex',
                    gap: '8px'
                  }}>
                    <button
                      onClick={() => onEditMemory(note)}
                      style={{
                        background: '#C4714A',
                        color: 'white',
                        border: 'none',
                        borderRadius: '50%',
                        width: '32px',
                        height: '32px',
                        fontSize: '14px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                      }}
                      title={t('notes.editMemory')}
                    >
                      ✏️
                    </button>

                    <button
                      onClick={() => onDeleteMemory(note.id)}
                      style={{
                        background: '#D85532',
                        color: 'white',
                        border: 'none',
                        borderRadius: '50%',
                        width: '32px',
                        height: '32px',
                        fontSize: '14px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                      }}
                      title={t('notes.deleteMemory')}
                    >
                      🗑️
                    </button>
                  </div>
                  )}

                  <h4 style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    color: activityColor || (note.type === 'todo' ? '#d68910' : '#8B7355'),
                    margin: '0 0 12px 0',
                    paddingRight: '80px', // Make room for both buttons
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    {note.type === 'todo' && <span style={{ fontSize: '16px' }}>✅</span>}
                    {note.title}
                  </h4>

                  {note.offRoute && (
                    <div
                      title={t('reveal.offRouteHint')}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        background: '#C8DCE5',
                        color: '#2D5A6E',
                        padding: '4px 10px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: 600,
                        margin: '0 0 12px 0'
                      }}
                    >
                      <span aria-hidden="true">🧭</span>
                      {t('reveal.offRoute')}
                    </div>
                  )}

                  <p style={{
                    fontSize: '16px',
                    lineHeight: '1.6',
                    color: '#333',
                    margin: '0 0 16px 0',
                    whiteSpace: 'pre-wrap'
                  }}>{note.content}</p>

                  {note.tags && note.tags.length > 0 && (
                    <div style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '8px',
                      marginBottom: '16px'
                    }}>
                      {note.tags.map((tag, tagIndex) => (
                        <span
                          key={tagIndex}
                          style={{
                            background: '#C4714A',
                            color: 'white',
                            padding: '6px 12px',
                            borderRadius: '16px',
                            fontSize: '12px',
                            fontWeight: '600'
                          }}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}

                  {note.photos && note.photos.length > 0 && (
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                      gap: '8px',
                      marginBottom: '16px'
                    }}>
                      {note.photos.map((photo, photoIndex) => (
                        <div
                          key={photoIndex}
                          style={{
                            borderRadius: '8px',
                            overflow: 'hidden',
                            aspectRatio: '1',
                            background: '#f0f0f0'
                          }}
                        >
                          <NotePhoto photo={photo} />
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Maps Button - Only for activity notes with maps link */}
                  {note.mapsLink && (
                    <div style={{
                      marginTop: '16px',
                      marginBottom: '16px'
                    }}>
                      <button
                        onClick={() => {
                          if (note.mapsLink) {
                            window.open(note.mapsLink, '_blank');
                          }
                        }}
                        style={{
                          background: 'linear-gradient(135deg, #27AE60 0%, #2ECC71 100%)',
                          color: 'white',
                          border: 'none',
                          padding: '12px 20px',
                          borderRadius: '25px',
                          fontSize: '14px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          boxShadow: '0 4px 12px rgba(39, 174, 96, 0.3)',
                          transition: 'all 0.2s ease',
                          width: 'fit-content'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.transform = 'translateY(-2px)';
                          e.target.style.boxShadow = '0 6px 16px rgba(39, 174, 96, 0.4)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.transform = 'translateY(0)';
                          e.target.style.boxShadow = '0 4px 12px rgba(39, 174, 96, 0.3)';
                        }}
                        title={t('notes.openMaps')}
                      >
                        <span style={{ fontSize: '16px' }}>📍</span>
                        {t('notes.openMaps')}
                      </button>
                    </div>
                  )}

                  <div style={{
                    fontSize: '12px',
                    color: '#999',
                    fontStyle: 'italic'
                  }}>
                    Written on {new Date(note.date).toLocaleTimeString('en-US', { 
                      hour: 'numeric', 
                      minute: '2-digit' 
                    })}
                  </div>
                </div>
                )
              })}
            </div>
          ) : (
            <div style={{
              textAlign: 'center',
              padding: '40px 20px',
              color: '#999',
              fontStyle: 'italic'
            }}>
              <div style={{ fontSize: '32px', marginBottom: '12px' }}>📝</div>
              <p>{t('notes.noMemories')}</p>
            </div>
          )}
        </div>

        {/* Challenges Section */}
        {getDayNotes(currentDay).filter(n => n.isChallenge === true).length > 0 && (
          <div style={{ marginBottom: '40px' }}>
            <h3 style={{
              fontSize: '20px',
              fontWeight: '700',
              color: '#8B7355',
              margin: '0 0 20px 0',
              borderBottom: '2px solid #f0f0f0',
              paddingBottom: '12px'
            }}>🏆 Desafíos</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {getDayNotes(currentDay).filter(n => n.isChallenge === true).map(note => (
                <ChallengeCard
                  key={note.id}
                  note={note}
                  onSubmitChallenge={onSubmitChallenge}
                  onDeleteMemory={onDeleteMemory}
                />
              ))}
            </div>
          </div>
        )}

        {/* Reflection Section */}
        <div>
          <h3 style={{
            fontSize: '20px',
            fontWeight: '700',
            color: '#8B7355',
            margin: '0 0 20px 0',
            borderBottom: '2px solid #f0f0f0',
            paddingBottom: '12px'
          }}>💭 {t('notes.dayReflection')}</h3>

          {getDayReflection(currentDay) ? (
            <div style={{
              background: '#f9f7f4',
              borderRadius: '16px',
              padding: '24px',
              border: '1px solid #e8e1d6'
            }}>
              {[
                { label: t('notes.reflectionLabel1'), emoji: '😊', answer: getDayReflection(currentDay).answers.feeling },
                { label: t('notes.reflectionLabel2'), emoji: '😮', answer: getDayReflection(currentDay).answers.surprise },
                { label: t('notes.reflectionLabel3'), emoji: '🙏', answer: getDayReflection(currentDay).answers.gratitude },
                { label: t('notes.reflectionLabel4'), emoji: '🌅', answer: getDayReflection(currentDay).answers.expectations }
              ].map((item, index) => (
                item.answer && (
                  <div key={index} style={{ marginBottom: '20px' }}>
                    <h4 style={{
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#8B7355',
                      margin: '0 0 8px 0',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <span>{item.emoji}</span>
                      {item.label}
                    </h4>
                    <p style={{
                      fontSize: '14px',
                      lineHeight: '1.5',
                      color: '#555',
                      margin: '0',
                      paddingLeft: '32px',
                      whiteSpace: 'pre-wrap'
                    }}>{item.answer}</p>
                  </div>
                )
              ))}
            </div>
          ) : (
            <div style={{
              textAlign: 'center',
              padding: '40px 20px',
              color: '#999',
              fontStyle: 'italic'
            }}>
              <div style={{ fontSize: '32px', marginBottom: '12px' }}>💭</div>
              <p>{t('notes.noReflection')}</p>
            </div>
          )}
        </div>
      </div>

      {/* Back Button */}
      <div style={{
        textAlign: 'center'
      }}>
        <button 
          style={{
            background: 'transparent',
            color: '#8B7355',
            border: '2px solid #8B7355',
            padding: '12px 24px',
            borderRadius: '25px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
          onClick={onBack}
        >
          {t('notes.backToNotes')}
        </button>
      </div>
    </div>
  )
}

// Challenge Submission Form Component
function ChallengeSubmissionForm({ challengeNote, onBack, onSubmit, userData }) {
  const { t } = useTranslation()
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    instagramLink: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submissionStatus, setSubmissionStatus] = useState(null) // null, 'success', 'error'

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!formData.name.trim() || !formData.email.trim()) {
      alert(t('challengeSubmit.missingFields'))
      return
    }

    setIsSubmitting(true)

    try {
      // Prepare submission data
      const submissionData = {
        challengeTitle: challengeNote.challengeData.title,
        challengeDescription: challengeNote.challengeData.description,
        challengeDifficulty: challengeNote.challengeData.difficulty,
        challengePoints: challengeNote.challengeData.points,
        participantName: formData.name,
        participantEmail: formData.email,
        instagramLink: formData.instagramLink,
        sharedUrl: formData.instagramLink, // Sprint 3 — public post (Instagram/TikTok), the proof
        destination: userData.destination,
        submissionDate: new Date().toISOString(),
        submissionId: Date.now().toString()
      }

      // Send to the DB-persisting endpoint (records the submission so the admin
      // review + check-status loop can find it; the root php only emailed).
      const response = await fetch('./api/submit-challenge.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData)
      })

      const result = await response.json()
      
      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Submission failed')
      }
      
      console.log('Challenge submission successful:', result)
      
      // Mark challenge as submitted in storage
      const tripData = Storage.getTripData()
      const challengeIndex = tripData.notes.findIndex(note => note.id === challengeNote.id)
      if (challengeIndex !== -1 && tripData.notes[challengeIndex].challengeData) {
        tripData.notes[challengeIndex].challengeData.submitted = true
        tripData.notes[challengeIndex].challengeData.submissionId = result.submissionId
        tripData.notes[challengeIndex].challengeData.submissionDate = new Date().toISOString()
        if (formData.instagramLink) tripData.notes[challengeIndex].challengeData.sharedUrl = formData.instagramLink
        Storage.saveTripData(tripData)
      }

      aggregateProfile().catch(() => {})
      setSubmissionStatus('success')
      setTimeout(() => {
        onSubmit()
      }, 3000)

    } catch (error) {
      console.error('Submission error:', error)
      setSubmissionStatus('error')
      setIsSubmitting(false)
    }
  }

  if (submissionStatus === 'success') {
    return (
      <div style={{ padding: '20px', maxWidth: '600px', margin: '0 auto' }}>
        <div style={{
          textAlign: 'center',
          padding: '40px',
          background: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
          borderRadius: '16px',
          color: 'white'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>🎉</div>
          <h2 style={{ margin: '0 0 16px 0', fontSize: '24px' }}>{t('challengeSubmit.successTitle')}</h2>
          <p style={{ margin: '0', fontSize: '16px', opacity: '0.9' }}>
            {t('challengeSubmit.successBody')}
          </p>
        </div>
      </div>
    )
  }

  if (submissionStatus === 'error') {
    return (
      <div style={{ padding: '20px', maxWidth: '600px', margin: '0 auto' }}>
        <div style={{
          textAlign: 'center',
          padding: '40px',
          background: '#f44336',
          borderRadius: '16px',
          color: 'white'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>❌</div>
          <h2 style={{ margin: '0 0 16px 0', fontSize: '24px' }}>{t('challengeSubmit.errorTitle')}</h2>
          <p style={{ margin: '0 0 20px 0', fontSize: '16px', opacity: '0.9' }}>
            {t('challengeSubmit.errorBody')}
          </p>
          <button
            onClick={() => setSubmissionStatus(null)}
            style={{
              background: 'white',
              color: '#f44336',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '25px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            {t('challengeSubmit.tryAgain')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div style={{ padding: '20px', maxWidth: '600px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '30px', textAlign: 'center' }}>
        <h1 style={{
          fontSize: '28px',
          fontWeight: '700',
          color: '#8B7355',
          margin: '0 0 8px 0'
        }}>🏆 {t('challengeSubmit.title')}</h1>
        <p style={{
          fontSize: '16px',
          color: '#666',
          margin: '0'
        }}>{challengeNote.challengeData.title}</p>
      </div>

      {/* Challenge Info */}
      <div style={{
        background: '#f9f7f4',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '30px',
        border: '1px solid #e8e1d6'
      }}>
        <h3 style={{ margin: '0 0 12px 0', color: '#8B7355' }}>{t('challengeSubmit.detailsHeading')}</h3>
        <p style={{ margin: '0 0 12px 0', color: '#333' }}>{challengeNote.challengeData.description}</p>
        <div style={{
          display: 'flex',
          gap: '16px',
          fontSize: '14px',
          color: '#666'
        }}>
          <span>💪 {challengeNote.challengeData.difficulty.toUpperCase()}</span>
        </div>
      </div>

      {/* How to take part — the rules the participant needs to know up front */}
      <div style={{
        background: '#fff7f2',
        border: '1.5px solid #E3C3B0',
        borderLeft: '4px solid #C4714A',
        borderRadius: '12px',
        padding: '18px 20px',
        marginBottom: '30px'
      }}>
        <h3 style={{ margin: '0 0 12px 0', color: '#A85B38', fontSize: '16px' }}>🏆 {t('challengeSubmit.howTitle', 'How to take part')}</h3>
        <ol style={{ margin: 0, paddingLeft: '20px', color: '#5d4f3f', fontSize: '14px', lineHeight: 1.7 }}>
          <li>{t('challengeSubmit.step1', 'Do the challenge for real.')}</li>
          <li>{t('challengeSubmit.step2', 'Wear your LOS DIAS t-shirt — it must be visible.')}</li>
          <li>{t('challengeSubmit.step3', 'Post it publicly on Instagram or TikTok (make sure the post is public).')}</li>
          <li>{t('challengeSubmit.step4', 'Paste the post link below and submit.')}</li>
        </ol>
        <p style={{ margin: '12px 0 0', color: '#8B7355', fontSize: '13px', fontStyle: 'italic' }}>
          {t('challengeSubmit.howOutro', "We'll review it. If it's great, we'll be in touch about your LOS DIAS reward.")}
        </p>
      </div>

      {/* Submission Form */}
      <form onSubmit={handleSubmit}>
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '24px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{
            margin: '0 0 20px 0',
            color: '#8B7355',
            fontSize: '18px'
          }}>📝 {t('challengeSubmit.yourDetails')}</h3>

          {/* Name Field */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: '600',
              color: '#333'
            }}>
              {t('challengeSubmit.fullName')} *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #e1e1e1',
                borderRadius: '8px',
                fontSize: '16px',
                boxSizing: 'border-box'
              }}
              placeholder={t('challengeSubmit.fullNamePlaceholder')}
              required
            />
          </div>

          {/* Email Field */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: '600',
              color: '#333'
            }}>
              {t('challengeSubmit.emailLabel')} *
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #e1e1e1',
                borderRadius: '8px',
                fontSize: '16px',
                boxSizing: 'border-box'
              }}
              placeholder={t('challengeSubmit.emailPlaceholder')}
              required
            />
          </div>

          {/* Public post link — Instagram OR TikTok */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: '600',
              color: '#333'
            }}>
              {t('challengeSubmit.postLabel', 'Public post link — Instagram or TikTok')}
            </label>
            <input
              type="url"
              value={formData.instagramLink}
              onChange={(e) => handleInputChange('instagramLink', e.target.value)}
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #e1e1e1',
                borderRadius: '8px',
                fontSize: '16px',
                boxSizing: 'border-box'
              }}
              placeholder="https://instagram.com/p/…  ·  https://tiktok.com/@…"
            />
            <div style={{
              fontSize: '12px',
              color: '#666',
              marginTop: '4px'
            }}>
              {t('challengeSubmit.postHelp', 'Link to your public Instagram or TikTok post showing the challenge with your LOS DIAS t-shirt.')}
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            style={{
              width: '100%',
              background: isSubmitting ? '#ccc' : 'linear-gradient(135deg, #B8A082 0%, #D4A574 100%)',
              color: 'white',
              border: 'none',
              padding: '16px',
              borderRadius: '25px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              marginBottom: '16px'
            }}
          >
            {isSubmitting ? `🔄 ${t('challengeSubmit.submitting')}` : `🚀 ${t('challengeSubmit.submit')}`}
          </button>

          {/* Back Button */}
          <button
            type="button"
            onClick={onBack}
            style={{
              width: '100%',
              background: 'transparent',
              color: '#8B7355',
              border: '2px solid #8B7355',
              padding: '12px',
              borderRadius: '25px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            ← {t('challengeSubmit.back')}
          </button>
        </div>
      </form>
    </div>
  )
}

export default Notes